import { Facebook, Phone, MapPin, Instagram, Mail, ExternalLink } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.12z" />
  </svg>
);

const telHref = (p) => `tel:${(p || "").replace(/[^0-9+]/g, "")}`;

export const Footer = () => {
  const content = useSiteContent();
  const locs = content?.locations || [];
  const memphis = locs.find((l) => l.id === "memphis") || locs[0] || {};
  const wm = locs.find((l) => l.id === "west-memphis") || locs[1] || {};
  const social = content?.social || {};

  return (
  <footer data-testid="footer" className="bg-black border-t-2 border-[#E60000] pb-24 sm:pb-16">
    {/* Brand bar */}
    <div className="bg-black border-b border-[#222]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3" data-testid="footer-brand">
          <img src="/assets/hillboyz-logo.png" alt="HillBoyz logo" className="w-12 h-12 object-contain" />
          <div className="hb-logo-text text-3xl sm:text-4xl">
            HILL<span className="accent">BOYZ</span>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">
          Wing &amp; Burger Bar · Two Locations · Since 2021
        </div>
      </div>
    </div>

    {/* Info grid */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-1">
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-4">About</div>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          Memphis wings, burgers, and the best banana pudding in the city — 11 years strong, family-run.
        </p>
        <a
          data-testid="footer-email"
          href="mailto:info@hillboyzmemphis.com"
          className="inline-flex items-center gap-2 text-sm text-white/85 hover:text-[#E60000] break-all"
        >
          <Mail className="w-4 h-4 text-[#E60000] flex-shrink-0" />
          info@hillboyzmemphis.com
        </a>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-4">Memphis · TN</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 text-[#E60000] flex-shrink-0" />
            <span>{memphis.address_line1 || "3711 Lamar Avenue"}<br />{memphis.address_line2 || "Memphis, TN 38118"}</span>
          </li>
          <li className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#E60000] flex-shrink-0" />
            <a data-testid="footer-phone-memphis" href={telHref(memphis.phone)} className="hover:text-[#E60000]">
              {memphis.phone || "(901) 623-7196"}
            </a>
          </li>
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-4">West Memphis · AR</div>
        <ul className="space-y-2 text-sm text-white/70">
          <li className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 text-[#E60000] flex-shrink-0" />
            <span>{wm.address_line1 || "109 S Worthington Drive"}<br />{wm.address_line2 || "West Memphis, AR 72301"}</span>
          </li>
          <li className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#E60000] flex-shrink-0" />
            <a data-testid="footer-phone-wm" href={telHref(wm.phone)} className="hover:text-[#E60000]">
              {wm.phone || "(870) 394-4229"}
            </a>
          </li>
        </ul>
        <div className="mt-4 text-xs text-white/50 font-mono leading-relaxed whitespace-pre-line">
          {(wm.hours_lines || ["Mon – Fri  10A – 10P", "Saturday  12P – 10P", "Sunday    Closed"]).join("\n")}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-4">@ilovehillboyz</div>
        <div className="flex gap-3 mb-5">
          {social.facebook && (
            <SocialBtn href={social.facebook} label="Facebook" testId="footer-social-fb">
              <Facebook className="w-5 h-5" strokeWidth={2.5} />
            </SocialBtn>
          )}
          {social.instagram && (
            <SocialBtn href={social.instagram} label="Instagram" testId="footer-social-ig">
              <Instagram className="w-5 h-5" strokeWidth={2.5} />
            </SocialBtn>
          )}
          {social.tiktok && (
            <SocialBtn href={social.tiktok} label="TikTok" testId="footer-social-tt">
              <TikTokIcon className="w-5 h-5" />
            </SocialBtn>
          )}
        </div>
        <div className="text-xs text-white/50 leading-relaxed">
          Order food: tap the floating <span className="text-[#E60000] font-bold">Order on DoorDash</span> button on any page.
        </div>
      </div>
    </div>

    {/* Bottom strip */}
    <div className="border-t border-[#222]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center md:justify-between gap-3 text-center md:text-left">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">
          © {new Date().getFullYear()} HillBoyz · Memphis &amp; West Memphis
        </div>
        <a
          data-testid="footer-traceelements"
          href="https://traceelements.dev/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-[#E60000] inline-flex items-center gap-1.5 group font-bold"
        >
          Powered by <span className="text-[#E60000] group-hover:underline">TraceElements</span>
          <ExternalLink className="w-3 h-3 opacity-70 group-hover:opacity-100" />
        </a>
      </div>
    </div>
  </footer>
  );
};

const SocialBtn = ({ href, label, children, testId }) => (
  <a
    data-testid={testId}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="w-11 h-11 flex items-center justify-center border-2 border-[#333] text-white hover:border-[#E60000] hover:bg-[#E60000] hover:text-white transition-colors"
  >
    {children}
  </a>
);

/**
 * Floating brand mark — fixed at bottom-LEFT corner of every page so the
 * HillBoyz name + logo always stays visible without clashing with the
 * DoorDash button (which is bottom-center).
 */
export const FloatingBrand = () => (
  <a
    href="/"
    data-testid="floating-brand"
    className="fixed bottom-3 left-3 z-40 hidden sm:inline-flex items-center gap-2 bg-black/85 backdrop-blur-md border-2 border-[#E60000] px-3 py-2 hover:bg-[#E60000] transition-colors group"
    style={{ boxShadow: "0 4px 20px rgba(230,0,0,0.35)" }}
    aria-label="HillBoyz home"
  >
    <img src="/assets/hillboyz-logo.png" alt="" className="w-7 h-7 object-contain" />
    <div className="hb-logo-text text-xl leading-none">
      HILL<span className="accent">BOYZ</span>
    </div>
  </a>
);
